import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api, { API, buildWaLink, formatIDR } from "../lib/api";
import { sendWaSmart } from "../lib/wa";
import Layout from "../components/Layout";
import OrderDocsModal from "./OrderDocsModal";
import VideoMergeModal from "./VideoMergeModal";
import OrderMobileCard from "./OrderMobileCard";
import { Plus, Pencil, Trash2, MessageCircle, Search, FileText, Send, X, FileSignature, Paperclip, FolderOpen, CalendarDays, List as ListIcon, ChevronLeft, ChevronRight, MapPin, Wrench } from "lucide-react";
import { toast } from "sonner";

const WORK_STATUSES = ["Dalam Penawaran", "Dijadwalkan", "Berjalan", "Selesai", "Gagal", "Komplainan", "Dibatalkan"];
const PAY_STATUSES = ["Belum Dibayar", "DP", "Lunas"];
const PAY_METHODS = ["Tunai", "Transfer", "QRIS", "Kartu", "Lainnya"];
const WARRANTY_OPTIONS = [
  { value: "", label: "Tanpa Garansi" },
  { value: "1w", label: "1 Minggu" },
  { value: "2w", label: "2 Minggu" },
  { value: "1m", label: "1 Bulan" },
  { value: "2m", label: "2 Bulan" },
  { value: "3m", label: "3 Bulan" },
];

const STATUS_COLORS = {
  "Dalam Penawaran": "bg-[#7C3AED]/10 text-[#7C3AED] border-[#7C3AED]/20",
  Dijadwalkan: "bg-[#2563EB]/10 text-[#2563EB] border-[#2563EB]/20",
  Berjalan: "bg-[#D97706]/10 text-[#D97706] border-[#D97706]/20",
  Selesai: "bg-[#059669]/10 text-[#059669] border-[#059669]/20",
  Gagal: "bg-[#991B1B]/10 text-[#991B1B] border-[#991B1B]/20",
  Komplainan: "bg-[#EA580C]/10 text-[#EA580C] border-[#EA580C]/20",
  Dibatalkan: "bg-[#DC2626]/10 text-[#DC2626] border-[#DC2626]/20",
};

// Add given period to date (YYYY-MM-DD), returns YYYY-MM-DD or null
const addWarrantyPeriod = (dateStr, period) => {
  if (!dateStr || !period) return null;
  const d = new Date(dateStr + "T00:00:00");
  if (Number.isNaN(d.getTime())) return null;
  const m = { "1w": [0, 7], "2w": [0, 14], "1m": [1, 0], "2m": [2, 0], "3m": [3, 0] };
  const [months, days] = m[period] || [0, 0];
  if (months) d.setMonth(d.getMonth() + months);
  if (days) d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
};

const warrantyInfo = (o) => {
  const end = addWarrantyPeriod(o.schedule_date, o.warranty_period);
  if (!end) return null;
  const today = new Date().toISOString().slice(0, 10);
  return { end, active: today <= end };
};

const emptyOrder = {
  customer_id: "",
  technician_id: "",
  assignments: [],
  items: [{ description: "", qty: 1, price: 0, point: 0, kind: "", disc_type: "", disc_value: 0 }],
  schedule_date: new Date().toISOString().slice(0, 10),
  work_status: "Dijadwalkan",
  payment_status: "Belum Dibayar",
  payment_method: "Tunai",
  bank_destination: "",
  warranty_period: "",
  related_order_id: "",
  dp_amount: 0,
  luar_kota: false,
  vehicle: "motor",
  work_sessions: [],
  notes: "",
};

const Orders = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const openHandledRef = useRef(false);
  const [list, setList] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [technicians, setTechnicians] = useState([]);
  const [leaveIdsForDate, setLeaveIdsForDate] = useState([]); // teknisi yang libur di form.schedule_date
  const [settings, setSettings] = useState({});
  const [search, setSearch] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [filterKind, setFilterKind] = useState("");
  const [sortBy, setSortBy] = useState("created_desc");
  const [catalog, setCatalog] = useState({ jasa: [], material: [] });
  const [modal, setModal] = useState(false);
  const [form, setForm] = useState(emptyOrder);
  const [editingId, setEditingId] = useState(null);
  const [editingOrder, setEditingOrder] = useState(null);
  const [drivePanelOpen, setDrivePanelOpen] = useState(false);
  const [driveSearch, setDriveSearch] = useState("");
  const [driveResults, setDriveResults] = useState([]);
  const [driveBusy, setDriveBusy] = useState(false);

  const searchDriveFolders = async () => {
    if (driveSearch.trim().length < 2) { toast.error("Ketik minimal 2 huruf nama folder"); return; }
    setDriveBusy(true);
    try {
      const { data } = await api.get("/drive/folders/search", { params: { q: driveSearch } });
      setDriveResults(data.folders || []);
      if ((data.folders || []).length === 0) toast.info("Folder tidak ditemukan di Drive");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Gagal mencari folder Drive");
    } finally {
      setDriveBusy(false);
    }
  };

  const pickDriveFolder = async (f) => {
    setDriveBusy(true);
    try {
      await api.post(`/orders/${editingId}/drive/set-folder`, { folder_id: f.id, folder_name: f.name });
      setEditingOrder((o) => ({ ...o, gdrive_folder_id: f.id, video_folder_id: f.id, video_folder_name: f.name }));
      setDrivePanelOpen(false); setDriveResults([]); setDriveSearch("");
      toast.success(`Folder terhubung: ${f.name}`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Gagal menghubungkan folder");
    } finally {
      setDriveBusy(false);
    }
  };

  const resetDriveFolder = async () => {
    setDriveBusy(true);
    try {
      await api.post(`/orders/${editingId}/drive/reset-folder`);
      setEditingOrder((o) => ({ ...o, gdrive_folder_id: "", video_folder_id: "", video_folder_name: "" }));
      toast.success("Kembali ke mode otomatis");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Gagal reset folder");
    } finally {
      setDriveBusy(false);
    }
  };
  const [confirmDel, setConfirmDel] = useState(null);
  const [videoMergeFor, setVideoMergeFor] = useState(null); // order yang sedang digabung videonya
  const [docsFor, setDocsFor] = useState(null);  // order whose Drive docs panel is open
  // Autocomplete pelanggan in modal
  const [custSearch, setCustSearch] = useState("");
  const [custOpen, setCustOpen] = useState(false);
  // Calendar view state (missing declarations restored by testing agent)
  const [view, setView] = useState("list");
  const _today = new Date();
  const [calMonth, setCalMonth] = useState(_today.getMonth());  const [calYear, setCalYear] = useState(_today.getFullYear());
  const [calOrders, setCalOrders] = useState([]);
  const [calTech, setCalTech] = useState("");
  const [calStatus, setCalStatus] = useState("");

  const load = async () => {
    const kindParams = filterKind.startsWith("item:") ? { item: filterKind.slice(5) } : { kind: filterKind };
    const { data } = await api.get("/orders", { params: { search, status: filterStatus, ...kindParams, sort: sortBy } });
    setList(data);
  };
  const loadRefs = async () => {
    const [c, t, s, cat] = await Promise.allSettled([
      api.get("/customers"), api.get("/technicians"), api.get("/settings"), api.get("/catalog"),
    ]);
    const cData = c.status === "fulfilled" ? c.value.data || [] : [];
    if (c.status === "fulfilled") setCustomers(cData);
    if (t.status === "fulfilled") setTechnicians(t.value.data || []);
    if (s.status === "fulfilled") setSettings(s.value.data || {});
    const all = cat.status === "fulfilled" ? cat.value.data || [] : [];
    setCatalog({
      jasa: all.filter(x => x.kind === "jasa"),
      material: all.filter(x => x.kind === "material"),
    });
    // After loading refs, react to URL params (?new=1&customer_id=...)
    const newFlag = searchParams.get("new");
    const cid = searchParams.get("customer_id");
    if (newFlag === "1") {
      const cust = cData.find((x) => x.id === cid);
      setForm({ ...emptyOrder, customer_id: cid || "" });
      setCustSearch(cust ? `${cust.name} · ${cust.phone}` : "");
      setEditingId(null);
      setModal(true);
      // Clear URL params so refresh doesn't reopen
      setSearchParams({}, { replace: true });
    }
    const openId = searchParams.get("open");
    if (openId && !openHandledRef.current) {
      openHandledRef.current = true;
      try {
        const { data } = await api.get(`/orders/${openId}`);
        openEdit(data);
      } catch (_) {
        toast.error("Order tidak ditemukan");
      }
      setSearchParams({}, { replace: true });
    }
  };

  useEffect(() => { loadRefs(); /* eslint-disable-next-line */ }, []);
  useEffect(() => {
    // Server-side customer search: supaya semua pelanggan (termasuk yang lama, di luar 2000 pertama) bisa ditemukan
    if (!custOpen) return;
    const t = setTimeout(() => {
      api.get("/customers", { params: { search: (custSearch || "").trim() } })
        .then((r) => setCustomers(r.data || []))
        .catch(() => {});
    }, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [custSearch, custOpen]);
  useEffect(() => {
    // Load teknisi yang libur di tanggal jadwal saat modal terbuka atau tanggal berubah
    if (!modal || !form.schedule_date) { setLeaveIdsForDate([]); return; }
    api.get(`/leaves/${form.schedule_date}`)
      .then((r) => setLeaveIdsForDate(r.data?.technician_ids || []))
      .catch(() => setLeaveIdsForDate([]));
  }, [modal, form.schedule_date]);
  useEffect(() => {
    const t = setTimeout(load, 200);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [search, filterStatus, filterKind, sortBy]);

  // Calendar: fetch orders scheduled in the visible month
  const toIso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  const loadCalOrders = async () => {
    const from = toIso(new Date(calYear, calMonth, 1));
    const to = toIso(new Date(calYear, calMonth + 1, 0));
    try {
      const { data } = await api.get("/orders", { params: { schedule_from: from, schedule_to: to, sort: "schedule_asc" } });
      setCalOrders(data || []);
    } catch { setCalOrders([]); }
  };
  useEffect(() => {
    if (view === "calendar") loadCalOrders();
    // eslint-disable-next-line
  }, [view, calMonth, calYear]);

  const ordersByDate = useMemo(() => {
    const m = {};
    calOrders.forEach((o) => {
      if (calStatus && o.work_status !== calStatus) return;
      const hasSessions = (o.work_sessions || []).length > 0;
      const sessions = hasSessions
        ? o.work_sessions
        : (o.schedule_date ? [{ date: o.schedule_date, technician_ids: null }] : []);
      sessions.forEach((s) => {
        if (!s.date) return;
        const sessTechIds = s.technician_ids === null || (s.technician_ids || []).length === 0
          ? (o.assignments || []).map((a) => a.technician_id).concat(o.technician_id ? [o.technician_id] : [])
          : s.technician_ids;
        if (calTech && !sessTechIds.includes(calTech)) return;
        (m[s.date] = m[s.date] || []).push({ ...o, _sessTechIds: sessTechIds });
      });
    });
    return m;
  }, [calOrders, calTech, calStatus]);

  const calFilteredCount = useMemo(() => Object.values(ordersByDate).reduce((s, arr) => s + arr.length, 0), [ordersByDate]);

  const calCells = useMemo(() => {
    const firstDow = new Date(calYear, calMonth, 1).getDay();
    const lastDate = new Date(calYear, calMonth + 1, 0).getDate();
    const arr = [];
    for (let i = 0; i < firstDow; i++) arr.push(null);
    for (let d = 1; d <= lastDate; d++) arr.push(new Date(calYear, calMonth, d));
    while (arr.length % 7 !== 0) arr.push(null);
    return arr;
  }, [calYear, calMonth]);

  const prevCalMonth = () => { if (calMonth === 0) { setCalMonth(11); setCalYear(calYear - 1); } else setCalMonth(calMonth - 1); };
  const nextCalMonth = () => { if (calMonth === 11) { setCalMonth(0); setCalYear(calYear + 1); } else setCalMonth(calMonth + 1); };


  const lineTotal = (i) => {
    const gross = Number(i.qty || 0) * Number(i.price || 0);
    const dv = Number(i.disc_value || 0);
    if (i.disc_type === "percent") return gross - gross * Math.min(Math.max(dv, 0), 100) / 100;
    if (i.disc_type === "amount") return gross - Math.min(Math.max(dv, 0), gross);
    return gross;
  };

  const total = useMemo(
    () => form.items.reduce((s, i) => s + lineTotal(i), 0),
    [form.items]
  );

  const filteredCustomers = useMemo(() => {
    const q = (custSearch || "").toLowerCase().trim();
    if (!q) return customers.slice(0, 50);
    return customers.filter((c) =>
      (c.name || "").toLowerCase().includes(q) ||
      (c.phone || "").toLowerCase().includes(q) ||
      (c.address || "").toLowerCase().includes(q)
    ).slice(0, 50);
  }, [customers, custSearch]);

  const selectedCustomerLabel = (id) => {
    const c = customers.find((x) => x.id === id);
    return c ? `${c.name} · ${c.phone}` : "";
  };

  const openCreate = () => { setForm(emptyOrder); setEditingId(null); setEditingOrder(null); setDrivePanelOpen(false); setCustSearch(""); setModal(true); };
  const openEdit = (o) => {
    const assigns = (o.assignments || []).map((a) => ({ technician_id: a.technician_id || "", fee: a.fee || 0 }));
    setEditingOrder(o);
    setDrivePanelOpen(false); setDriveSearch(""); setDriveResults([]);
    // Pakai data customer hasil enrich dari API orders (tidak bergantung list customers yang dibatasi 2000)
    setCustSearch(o.customer ? `${o.customer.name} · ${o.customer.phone}` : selectedCustomerLabel(o.customer_id));
    // Jika list teknisi kosong (request awal gagal), muat ulang referensi
    if (technicians.length === 0) loadRefs();
    setForm({
      customer_id: o.customer_id || "",
      technician_id: o.technician_id || "",
      assignments: assigns,
      items: (o.items && o.items.length ? o.items : [{ description: "", qty: 1, price: 0 }]),
      schedule_date: o.schedule_date || new Date().toISOString().slice(0, 10),
      work_status: o.work_status || "Dijadwalkan",
      payment_status: o.payment_status || "Belum Dibayar",
      payment_method: o.payment_method || "Tunai",
      bank_destination: o.bank_destination || "",
      warranty_period: o.warranty_period || "",
      related_order_id: o.related_order_id || "",
      dp_amount: o.dp_amount || 0,
      luar_kota: !!o.luar_kota,
      vehicle: o.vehicle || "motor",
      work_sessions: (o.work_sessions || []).map((s) => ({ date: s.date || "", technician_ids: (s.technician_ids || []).filter(Boolean) })),
      notes: o.notes || "",
    });
    setEditingId(o.id); setModal(true);
  };

  const addAssignment = () => setForm((f) => ({ ...f, assignments: [...(f.assignments || []), { technician_id: "", fee: 0 }] }));
  const setAssignment = (idx, patch) => setForm((f) => ({ ...f, assignments: (f.assignments || []).map((it, i) => i === idx ? { ...it, ...patch } : it) }));
  const removeAssignment = (idx) => setForm((f) => ({ ...f, assignments: (f.assignments || []).filter((_, i) => i !== idx) }));

  const addSession = () => setForm((f) => ({
    ...f,
    work_sessions: [
      ...(f.work_sessions || []),
      {
        date: (f.work_sessions || []).length === 0 ? (f.schedule_date || new Date().toISOString().slice(0, 10)) : "",
        technician_ids: (f.assignments || []).map((a) => a.technician_id).filter(Boolean),
      },
    ],
  }));
  const setSessionDate = (idx, date) => setForm((f) => ({ ...f, work_sessions: f.work_sessions.map((s, i) => (i === idx ? { ...s, date } : s)) }));
  const toggleSessionTech = (idx, tid) => setForm((f) => ({
    ...f,
    work_sessions: f.work_sessions.map((s, i) => {
      if (i !== idx) return s;
      const has = (s.technician_ids || []).includes(tid);
      return { ...s, technician_ids: has ? s.technician_ids.filter((x) => x !== tid) : [...(s.technician_ids || []), tid] };
    }),
  }));
  const removeSession = (idx) => setForm((f) => ({ ...f, work_sessions: (f.work_sessions || []).filter((_, i) => i !== idx) }));
  const splitFeeEvenly = () => {
    const totalOrder = form.items.reduce((s, i) => s + lineTotal(i), 0);
    const n = (form.assignments || []).length;
    if (n === 0) { toast.error("Tambahkan teknisi dulu"); return; }
    const share = Math.round(totalOrder / n);
    setForm((f) => ({ ...f, assignments: f.assignments.map((a) => ({ ...a, fee: share })) }));
    toast.success(`Fee dibagi rata: ${share.toLocaleString("id-ID")} × ${n}`);
  };

  const setItem = (idx, patch) => {
    const items = form.items.map((it, i) => i === idx ? { ...it, ...patch } : it);
    setForm({ ...form, items });
  };
  const addItem = () => setForm({ ...form, items: [...form.items, { description: "", qty: 1, price: 0, point: 0, kind: "", disc_type: "", disc_value: 0 }] });
  const removeItem = (idx) => setForm({ ...form, items: form.items.filter((_, i) => i !== idx) });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.customer_id) { toast.error("Pilih pelanggan dulu"); return; }
    if (form.items.length === 0) { toast.error("Tambahkan minimal 1 item"); return; }
    if (form.payment_method === "Transfer" && !form.bank_destination) {
      toast.error("Pilih bank tujuan transfer dulu");
      return;
    }
    const payload = {
      ...form,
      bank_destination: form.payment_method === "Transfer" ? form.bank_destination : "",
      work_sessions: (form.work_sessions || []).filter((s) => (s.date || "").trim()).map((s) => ({ date: s.date, technician_ids: (s.technician_ids || []).filter(Boolean) })),
      items: form.items.map((i) => ({ description: i.description, qty: Number(i.qty), price: Number(i.price), point: Number(i.point) || 0, kind: i.kind || "", disc_type: i.disc_type || "", disc_value: Number(i.disc_value) || 0 })),
    };
    try {
      if (editingId) {
        await api.put(`/orders/${editingId}`, payload);
        toast.success("Order diperbarui");
      } else {
        await api.post("/orders", payload);
        toast.success("Order dibuat");
      }
      setModal(false); load();
      if (view === "calendar") loadCalOrders();
    } catch (err) {
      toast.error("Gagal menyimpan order");
    }
  };

  const remove = async () => {
    if (!confirmDel) return;
    await api.delete(`/orders/${confirmDel.id}`);
    setConfirmDel(null);
    toast.success("Order dihapus");
    load();
    if (view === "calendar") loadCalOrders();
  };

  const sendStatusWa = (o) => {
    const itemsText = (o.items || []).map(i => `• ${i.description} (${i.qty}x)`).join("\n");
    const text = `Halo KK ${o.customer?.name || ""}, update untuk order *${o.order_no}*:\n\nLayanan:\n${itemsText}\n\nJadwal: ${o.schedule_date}\nStatus: *${o.work_status}*\nStatus Pembayaran: ${o.payment_status}\n\nTerima kasih.`;
    sendWaSmart({ enabled: settings.wa_gateway_enabled, phone: o.customer?.phone, text, name: o.customer?.name || "" });
  };

  const sendScheduleToTech = async (o) => {
    const fromAssignments = (o.assignments || []).map((a) => a.technician).filter((t) => t && (t.phone || "").trim());
    const techList = fromAssignments.length ? fromAssignments : (o.technician?.phone ? [o.technician] : []);
    if (!techList.length) { toast.error("Teknisi belum ditugaskan / nomor kosong"); return; }
    const itemsText = (o.items || []).map(i => `• ${i.description} (${i.qty}x)`).join("\n");
    const loc = (o.customer?.location_link || o.customer?.maps_link || "").trim();
    const mapsHref = loc || `https://maps.google.com/?q=${encodeURIComponent(o.customer?.address || o.customer?.name || "")}`;
    const seen = new Set();
    for (const t of techList) {
      if (seen.has(t.phone)) continue;
      seen.add(t.phone);
      const text = `Halo ${t.name}, ada penugasan baru:\n\nOrder: *${o.order_no}*\nPelanggan: ${o.customer?.name}\nTelepon: ${o.customer?.phone}\nAlamat: ${o.customer?.address || "-"}\n📍 *Google Maps:* ${mapsHref}\nJadwal: *${o.schedule_date}*\n\nPekerjaan:\n${itemsText}\n\nCatatan: ${o.notes || "-"}`;
      await sendWaSmart({ enabled: settings.wa_gateway_enabled, phone: t.phone, text, name: t.name || "" });
    }
  };

  const sendInvoice = async (o) => {
    const isTransfer = (o.payment_method || "").toLowerCase() === "transfer";
    const dest = (o.bank_destination || "").trim();
    const showBank1 = !isTransfer || dest === "" || dest === "1";
    const showBank2 = !isTransfer || dest === "" || dest === "2";
    const block1 = (showBank1 && (settings.bank_name || settings.bank_account_no || settings.bank_account_holder))
      ? `${settings.bank_name || ""}${settings.bank_account_no ? `\nNo. Rek: ${settings.bank_account_no}` : ""}${settings.bank_account_holder ? `\na.n. ${settings.bank_account_holder}` : ""}`
      : "";
    const block2 = (showBank2 && (settings.bank_name_2 || settings.bank_account_no_2 || settings.bank_account_holder_2))
      ? `${settings.bank_name_2 || ""}${settings.bank_account_no_2 ? `\nNo. Rek: ${settings.bank_account_no_2}` : ""}${settings.bank_account_holder_2 ? `\na.n. ${settings.bank_account_holder_2}` : ""}`
      : "";
    const bankBlocks = [block1, block2].filter(Boolean).join("\n\n");
    const bankSection = bankBlocks ? `\n\n*REKENING PEMBAYARAN*\n${bankBlocks}` : "";
    const tmpl = settings.payment_link_template || "";
    const payLink = (settings.doku_enabled && tmpl)
      ? tmpl.replace(/\{order_no\}/g, o.order_no || "").replace(/\{total\}/g, String(o.total || 0))
      : "";
    const payBlock = payLink ? `\n\n💳 *Bayar Online:* ${payLink}` : "";

    // Try to upload PDF to Drive (so customer can click without admin login).
    // If Drive not connected, send WA without PDF link (graceful fallback).
    let pdfBlock = "";
    const tid = toast.loading("Menyiapkan invoice…");
    try {
      const { data } = await api.post(`/orders/${o.id}/invoice/upload-to-drive`);
      if (data?.link) pdfBlock = `\n\n📄 *Invoice PDF:* ${data.link}`;
      toast.success("Invoice ter-upload ke Drive", { id: tid });
    } catch (err) {
      // 400 = Drive not connected — silently fall back
      if (err?.response?.status === 400) {
        toast.dismiss(tid);
      } else {
        toast.error(err?.response?.data?.detail || "Gagal upload PDF — pesan WA tetap dikirim tanpa link", { id: tid });
      }
    }

    const text = `Halo KK ${o.customer?.name || ""}, berikut invoice untuk order *${o.order_no}*.\n\nTotal: *${formatIDR(o.total)}*\nStatus: ${o.payment_status}\nMetode: ${o.payment_method}${bankSection}${payBlock}${pdfBlock}\n\nTerima kasih.`;
    const rekFlat = [
      bankBlocks.replace(/\n/g, " "),
      payLink ? `Bayar Online: ${payLink}` : "",
      pdfBlock ? pdfBlock.replace(/\n/g, " ").replace(/📄 \*Invoice PDF:\*/, "Invoice PDF:").trim() : "",
    ].filter(Boolean).join(" | ") || "-";
    sendWaSmart({
      enabled: settings.wa_gateway_enabled, phone: o.customer?.phone, text, name: o.customer?.name || "",
      template: "invoice_pelanggan",
      params: [o.customer?.name || "-", o.order_no || "-", formatIDR(o.total), o.payment_status || "-", o.payment_method || "-", rekFlat],
    });
  };

  const openInvoicePdf = (o) => {
    const token = localStorage.getItem("om_token");
    window.open(`${API}/orders/${o.id}/invoice.pdf?token=${encodeURIComponent(token || "")}`, "_blank");
  };

  const openQuotationPdf = (o) => {
    const token = localStorage.getItem("om_token");
    window.open(`${API}/orders/${o.id}/quotation.pdf?token=${encodeURIComponent(token || "")}`, "_blank");
  };

  const sendQuotationWa = async (o) => {
    const itemsText = (o.items || []).map(i => {
      const disc = i.disc_type === "percent" && Number(i.disc_value) > 0 ? ` (disc ${Number(i.disc_value)}%)` : i.disc_type === "amount" && Number(i.disc_value) > 0 ? ` (disc ${formatIDR(i.disc_value)})` : "";
      return `• ${i.description} (${i.qty}x @ ${formatIDR(i.price)})${disc}`;
    }).join("\n");
    // Try Drive-hosted link first; fallback to no link if not connected.
    let linkBlock = "";
    const tid = toast.loading("Menyiapkan penawaran…");
    try {
      const { data } = await api.post(`/orders/${o.id}/quotation/upload-to-drive`);
      if (data?.link) linkBlock = `\n\nDetail lengkap (PDF): ${data.link}`;
      toast.success("Penawaran ter-upload ke Drive", { id: tid });
    } catch (err) {
      if (err?.response?.status === 400) {
        toast.dismiss(tid);
        toast.error("Drive belum terhubung — link PDF tidak disertakan. Hubungkan di Pengaturan untuk fitur ini.");
      } else {
        toast.error(err?.response?.data?.detail || "Gagal upload PDF", { id: tid });
      }
    }
    const text = `Halo KK ${o.customer?.name || ""}, berikut penawaran untuk *${o.order_no}*:\n\n${itemsText}\n\n*Total Penawaran: ${formatIDR(o.total)}*${linkBlock}\n\nMohon konfirmasi jika menyetujui. Terima kasih.`;
    sendWaSmart({ enabled: settings.wa_gateway_enabled, phone: o.customer?.phone, text, name: o.customer?.name || "" });
  };

  return (
    <Layout>
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-6 sm:mb-8">
        <div>
          <div className="label-cap mb-2">Operasi Harian</div>
          <h1 className="font-heading text-2xl sm:text-3xl md:text-4xl font-black tracking-tight">Order</h1>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="inline-flex border border-[#E4E4E7] bg-white">
            <button type="button" data-testid="orders-view-list" onClick={() => setView("list")} className={`px-3 py-2 text-xs font-bold flex items-center gap-1 min-h-[44px] ${view === "list" ? "bg-[#002FA7] text-white" : ""}`}><ListIcon size={14} /> List</button>
            <button type="button" data-testid="orders-view-calendar" onClick={() => setView("calendar")} className={`px-3 py-2 text-xs font-bold flex items-center gap-1 min-h-[44px] ${view === "calendar" ? "bg-[#002FA7] text-white" : ""}`}><CalendarDays size={14} /> Kalender</button>
          </div>
          <button onClick={openCreate} data-testid="add-order-button" className="bg-[#002FA7] hover:bg-[#00227A] text-white text-xs font-bold uppercase tracking-wider px-4 py-3 flex items-center justify-center gap-2 min-h-[44px]">
            <Plus size={14} /> Order Baru
          </button>
        </div>
      </div>

      {view === "list" && (
      <div className="bg-white border border-[#E4E4E7]">
        <div className="px-4 py-3 border-b border-[#E4E4E7] flex items-center gap-3 flex-wrap">
          <div className="flex items-center gap-2 flex-1 min-w-[200px]">
            <Search size={14} className="text-[#52525B]" />
            <input
              data-testid="order-search-input"
              value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder="Cari order, pelanggan, teknisi…"
              className="flex-1 outline-none text-sm bg-transparent"
            />
          </div>
          <select data-testid="order-status-filter" value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} className="text-xs uppercase tracking-wider px-3 py-1.5 border border-[#E4E4E7] font-bold">
            <option value="">Semua Status</option>
            {WORK_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
          <select data-testid="order-kind-filter" value={filterKind} onChange={(e) => setFilterKind(e.target.value)} className="text-xs uppercase tracking-wider px-3 py-1.5 border border-[#E4E4E7] font-bold max-w-[220px]">
            <option value="">Semua Tipe</option>
            <optgroup label="── JASA ──">
              <option value="jasa">Semua Jasa</option>
              {(catalog.jasa || []).map((c) => <option key={c.id} value={`item:${c.name}`}>{c.name}</option>)}
            </optgroup>
            <optgroup label="── PRODUK / MATERIAL ──">
              <option value="material">Semua Produk</option>
              {(catalog.material || []).map((c) => <option key={c.id} value={`item:${c.name}`}>{c.name}</option>)}
            </optgroup>
          </select>
          <select data-testid="order-sort-select" value={sortBy} onChange={(e) => setSortBy(e.target.value)} className="text-xs uppercase tracking-wider px-3 py-1.5 border border-[#E4E4E7] font-bold">
            <option value="created_desc">Terbaru Dibuat</option>
            <option value="created_asc">Terlama Dibuat</option>
            <option value="schedule_desc">Jadwal ↓ Terbaru</option>
            <option value="schedule_asc">Jadwal ↑ Terlama</option>
            <option value="total_desc">Total Tertinggi</option>
            <option value="total_asc">Total Terendah</option>
          </select>
        </div>
        <div className="overflow-x-auto scroll-thin hidden md:block">
          <table className="w-full min-w-[1000px]">
            <thead>
              <tr className="border-b border-[#E4E4E7]">
                <th className="text-left label-cap px-4 py-3">No</th>
                <th className="text-left label-cap px-4 py-3">Pelanggan</th>
                <th className="text-left label-cap px-4 py-3">Kota</th>
                <th className="text-left label-cap px-4 py-3">Teknisi</th>
                <th className="text-left label-cap px-4 py-3">Jadwal</th>
                <th className="text-left label-cap px-4 py-3">Status</th>
                <th className="text-left label-cap px-4 py-3">Bayar</th>
                <th className="text-right label-cap px-4 py-3">Total</th>
                <th className="text-right label-cap px-4 py-3">Aksi</th>
              </tr>
            </thead>
            <tbody>
              {list.length === 0 && <tr><td colSpan="9" className="p-8 text-center text-sm text-[#52525B]">Belum ada order.</td></tr>}
              {list.map((o) => (
                <tr key={o.id} className="border-b border-[#E4E4E7] row-hover" data-testid={`order-row-${o.id}`}>
                  <td className="px-4 py-3 text-xs font-mono">
                    {o.order_no}
                    {(o.jasa_labels || []).length > 0 && (
                      <div className="flex flex-col gap-1 mt-1 items-start">
                        {o.jasa_labels.map((lbl, i) => (
                          <span key={i} className="text-[8px] font-sans font-bold px-1.5 py-0.5 border bg-[#2563EB]/10 text-[#2563EB] border-[#2563EB]/20 tracking-wide uppercase" data-testid={`order-badge-jasa-${o.id}`}>{lbl}</span>
                        ))}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3 text-sm font-bold">
                    {o.customer?.name || "-"}
                    {(() => {
                      const loc = (o.customer?.location_link || o.customer?.maps_link || "").trim() || (o.customer?.latitude && o.customer?.longitude ? `https://maps.google.com/?q=${o.customer.latitude},${o.customer.longitude}` : "");
                      if (!loc) return null;
                      return (
                        <a href={loc} target="_blank" rel="noreferrer" className="flex items-center gap-1 text-[10px] font-normal text-[#002FA7] hover:underline mt-0.5" data-testid={`order-shareloc-${o.id}`}>
                          <MapPin size={10} className="shrink-0" /> Shareloc GMaps
                        </a>
                      );
                    })()}
                  </td>
                  <td className="px-4 py-3 text-sm text-[#52525B]">{o.customer?.city || "—"}</td>
                  <td className="px-4 py-3 text-sm text-[#52525B]">
                    {(o.assignments && o.assignments.length > 0)
                      ? o.assignments.map((a, i) => (
                          <div key={i} className="text-xs">{a.technician?.name || "—"}{a.fee > 0 && <span className="text-[#52525B] font-mono ml-1">· {formatIDR(a.fee)}</span>}</div>
                        ))
                      : "—"}
                  </td>
                  <td className="px-4 py-3 text-xs font-mono">
                    {o.schedule_date}
                    {(o.work_sessions || []).length > 1 && (
                      <div className="text-[9px] text-[#7C3AED] font-bold font-sans" data-testid={`order-multiday-${o.id}`}>+{o.work_sessions.length - 1} hari lanjutan</div>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`text-xs font-bold px-2 py-1 border ${STATUS_COLORS[o.work_status]}`}>{o.work_status}</span>
                    {(() => {
                      const w = warrantyInfo(o);
                      if (!w) return null;
                      return (
                        <div className={`text-[9px] mt-1 font-mono ${w.active ? "text-[#059669]" : "text-[#DC2626]"}`} data-testid={`order-warranty-${o.id}`}>
                          {w.active ? "Garansi aktif" : "Garansi habis"} · {w.end}
                        </div>
                      );
                    })()}
                  </td>
                  <td className="px-4 py-3 text-xs">
                    {o.payment_status}
                    {o.payment_status === "DP" && Number(o.dp_amount || 0) > 0 && (
                      <div className="text-[9px] mt-1 font-mono text-[#52525B]" data-testid={`order-dp-info-${o.id}`}>
                        Dibayar: {formatIDR(o.dp_amount)}<br />
                        <span className="text-[#DC2626]">Sisa: {formatIDR(Math.max(0, (o.total || 0) - (o.dp_amount || 0)))}</span>
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3 text-sm font-mono font-bold text-right">{formatIDR(o.total)}</td>
                  <td className="px-4 py-3 text-right">
                    <div className="inline-flex gap-1">
                      <button onClick={() => sendStatusWa(o)} title="Kirim status ke pelanggan" data-testid={`order-wa-status-${o.id}`} className="p-2 hover:bg-[#059669]/10 text-[#059669] border border-[#E4E4E7]"><MessageCircle size={14} /></button>
                      <button onClick={() => sendScheduleToTech(o)} title="Kirim jadwal ke teknisi" data-testid={`order-wa-tech-${o.id}`} className="p-2 hover:bg-[#2563EB]/10 text-[#2563EB] border border-[#E4E4E7]"><Send size={14} /></button>
                      <button onClick={() => sendQuotationWa(o)} title="Kirim Penawaran via WhatsApp" data-testid={`order-wa-quotation-${o.id}`} className="p-2 hover:bg-[#7C3AED]/10 text-[#7C3AED] border border-[#E4E4E7]"><FileSignature size={14} /></button>
                      <button onClick={() => openQuotationPdf(o)} title="Lihat PDF Penawaran" data-testid={`order-quotation-pdf-${o.id}`} className="p-2 hover:bg-[#F4F4F5] border border-[#E4E4E7] text-[10px] font-bold text-[#7C3AED]">QUOT</button>
                      <button onClick={() => sendInvoice(o)} title="Kirim invoice WA" data-testid={`order-wa-invoice-${o.id}`} className="p-2 hover:bg-[#002FA7]/10 text-[#002FA7] border border-[#E4E4E7]"><FileText size={14} /></button>
                      <button onClick={() => openInvoicePdf(o)} title="Lihat PDF" data-testid={`order-pdf-${o.id}`} className="p-2 hover:bg-[#F4F4F5] border border-[#E4E4E7] text-[10px] font-bold">PDF</button>
                      <button onClick={() => setDocsFor(o)} title="Dokumentasi (Drive)" data-testid={`order-docs-${o.id}`} className="p-2 hover:bg-[#F4F4F5] border border-[#E4E4E7] text-[#52525B]"><Paperclip size={14} /></button>
                      <button onClick={() => openEdit(o)} title="Edit" data-testid={`order-edit-${o.id}`} className="p-2 hover:bg-[#F4F4F5] border border-[#E4E4E7]"><Pencil size={14} /></button>
                      <button onClick={() => setConfirmDel(o)} title="Hapus" data-testid={`order-delete-${o.id}`} className="p-2 hover:bg-[#DC2626]/10 text-[#DC2626] border border-[#E4E4E7]"><Trash2 size={14} /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Mobile order cards */}
        <div className="md:hidden">
          {list.length === 0 && <div className="p-8 text-center text-sm text-[#52525B]">Belum ada order.</div>}
          {list.map((o) => (
            <OrderMobileCard
              key={o.id}
              o={o}
              onStatusWa={() => sendStatusWa(o)}
              onTechWa={() => sendScheduleToTech(o)}
              onQuotWa={() => sendQuotationWa(o)}
              onQuotPdf={() => openQuotationPdf(o)}
              onInvoiceWa={() => sendInvoice(o)}
              onInvoicePdf={() => openInvoicePdf(o)}
              onDocs={() => setDocsFor(o)}
              onEdit={() => openEdit(o)}
              onDelete={() => setConfirmDel(o)}
            />
          ))}
        </div>
      </div>
      )}

      {view === "calendar" && (
        <>
          <div className="flex items-center gap-2 mb-4 flex-wrap">
            <button onClick={prevCalMonth} className="p-2 border border-[#E4E4E7] bg-white hover:bg-[#F4F4F5]" data-testid="orders-prev-month"><ChevronLeft size={16} /></button>
            <div className="px-4 py-2 border border-[#E4E4E7] bg-white text-sm font-bold min-w-[160px] text-center" data-testid="orders-month-label">
              {["Januari", "Februari", "Maret", "April", "Mei", "Juni", "Juli", "Agustus", "September", "Oktober", "November", "Desember"][calMonth]} {calYear}
            </div>
            <button onClick={nextCalMonth} className="p-2 border border-[#E4E4E7] bg-white hover:bg-[#F4F4F5]" data-testid="orders-next-month"><ChevronRight size={16} /></button>
            <select data-testid="orders-cal-tech-filter" value={calTech} onChange={(e) => setCalTech(e.target.value)} className="text-xs uppercase tracking-wider px-3 py-2 border border-[#E4E4E7] font-bold bg-white">
              <option value="">Semua Teknisi</option>
              {technicians.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
            <select data-testid="orders-cal-status-filter" value={calStatus} onChange={(e) => setCalStatus(e.target.value)} className="text-xs uppercase tracking-wider px-3 py-2 border border-[#E4E4E7] font-bold bg-white">
              <option value="">Semua Status</option>
              {WORK_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
            {(calTech || calStatus) && (
              <button data-testid="orders-cal-filter-reset" onClick={() => { setCalTech(""); setCalStatus(""); }} className="text-xs font-bold px-3 py-2 border border-[#DC2626]/30 text-[#DC2626] bg-white hover:bg-[#DC2626]/5 flex items-center gap-1"><X size={12} /> Reset</button>
            )}
            <div className="text-xs text-[#52525B] ml-2 hidden sm:block" data-testid="orders-cal-count">{(calTech || calStatus) ? `${calFilteredCount} dari ${calOrders.length}` : calOrders.length} order terjadwal bulan ini</div>
          </div>
          <div className="bg-white border border-[#E4E4E7] overflow-x-auto" data-testid="orders-calendar">
            <div className="min-w-[840px]">
              <div className="grid grid-cols-7 border-b border-[#E4E4E7]">
                {["Min", "Sen", "Sel", "Rab", "Kam", "Jum", "Sab"].map((d, i) => (
                  <div key={i} className={`px-3 py-2 label-cap ${i === 0 ? "text-[#DC2626]" : ""}`}>{d}</div>
                ))}
              </div>
              <div className="grid grid-cols-7">
                {calCells.map((d, i) => {
                  if (!d) return <div key={i} className="min-h-[110px] border-b border-r border-[#F4F4F5] bg-[#FAFAFA]" />;
                  const iso = toIso(d);
                  const dayOrders = ordersByDate[iso] || [];
                  const isToday = iso === toIso(new Date());
                  const isSun = d.getDay() === 0;
                  return (
                    <div key={i} data-testid={`orders-cell-${iso}`} className={`min-h-[110px] border-b border-r border-[#F4F4F5] p-1.5 ${isToday ? "bg-[#EFF6FF]" : ""}`}>
                      <div className={`text-sm font-bold px-1 ${isSun ? "text-[#DC2626]" : ""}`}>{d.getDate()}</div>
                      <div className="mt-1 space-y-1">
                        {dayOrders.map((o) => {
                          const job = (o.items || []).map((it) => it.description).filter(Boolean).join(", ") || `${o.order_no} (tanpa deskripsi)`;
                          let techNames = (o._sessTechIds || []).map((id) => technicians.find((t) => t.id === id)?.name || (o.assignments || []).find((a) => a.technician_id === id)?.technician?.name).filter(Boolean);
                          techNames = [...new Set(techNames)];
                          if (techNames.length === 0 && o.technician?.name) techNames.push(o.technician.name);
                          return (
                            <button
                              key={o.id}
                              onClick={() => openEdit(o)}
                              data-testid={`orders-cal-item-${o.id}`}
                              title={`${o.order_no} · ${o.customer?.name || ""} · ${o.work_status}${techNames.length ? ` · ${techNames.join(", ")}` : ""}`}
                              className={`w-full text-left px-1.5 py-1 border text-[10px] leading-tight hover:opacity-80 transition ${STATUS_COLORS[o.work_status] || "bg-[#F4F4F5] border-[#E4E4E7]"}`}
                            >
                              <div className="font-bold line-clamp-2">{job}</div>
                              <div className="line-clamp-1 opacity-90 font-bold" data-testid={`orders-cal-cust-${o.id}`}>{o.customer?.name || "—"}</div>
                              <div className="flex items-center gap-0.5 font-mono opacity-80"><MapPin size={9} className="shrink-0" /> {o.customer?.city || "—"}</div>
                              {techNames.length > 0 && (
                                <div className="flex items-center gap-0.5 opacity-90 mt-0.5" data-testid={`orders-cal-tech-${o.id}`}><Wrench size={9} className="shrink-0" /> <span className="line-clamp-1">{techNames.join(", ")}</span></div>
                              )}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </>
      )}

      {modal && (
        <div className="fixed inset-0 bg-black/40 flex items-stretch md:items-start justify-center md:p-4 z-50 overflow-y-auto" onClick={() => setModal(false)}>
          <form onClick={(e) => e.stopPropagation()} onSubmit={submit} className="bg-white border border-[#E4E4E7] w-full md:max-w-4xl md:my-8 flex flex-col min-h-screen md:min-h-0" data-testid="order-form">
            <div className="px-4 sm:px-6 py-4 border-b border-[#E4E4E7] flex items-center justify-between sticky top-0 bg-white z-10">
              <div className="min-w-0">
                <div className="label-cap">{editingId ? "Ubah" : "Tambah"} Order</div>
                <h2 className="font-heading text-xl sm:text-2xl font-bold truncate">Detail Pekerjaan</h2>
              </div>
              <button type="button" onClick={() => setModal(false)} className="p-2 hover:bg-[#F4F4F5]"><X size={20} /></button>
            </div>
            <div className="p-4 sm:p-6 grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="Pelanggan" required>
                <div className="relative">
                  <input
                    data-testid="order-customer-autocomplete"
                    type="text"
                    value={custSearch}
                    onChange={(e) => { setCustSearch(e.target.value); setForm({ ...form, customer_id: "" }); setCustOpen(true); }}
                    onFocus={() => { if (!form.customer_id) setCustOpen(true); }}
                    onBlur={() => setTimeout(() => setCustOpen(false), 150)}
                    placeholder="Ketik nama / telepon / alamat pelanggan…"
                    autoComplete="off"
                    className="w-full px-3 py-2 border border-[#E4E4E7] text-sm bg-white outline-none focus:border-[#002FA7]"
                  />
                  {/* hidden input enforces required */}
                  <input type="hidden" required value={form.customer_id} readOnly />
                  {custOpen && filteredCustomers.length > 0 && (
                    <div className="absolute top-full left-0 right-0 z-20 mt-1 max-h-64 overflow-auto bg-white border border-[#E4E4E7] shadow-lg" data-testid="order-customer-dropdown">
                      {filteredCustomers.map((c) => (
                        <button
                          type="button"
                          key={c.id}
                          data-testid={`order-customer-option-${c.id}`}
                          onClick={() => { setForm({ ...form, customer_id: c.id }); setCustSearch(`${c.name} · ${c.phone}`); setCustOpen(false); }}
                          className="w-full text-left px-3 py-2 text-sm hover:bg-[#F4F4F5] border-b border-[#E4E4E7] last:border-b-0"
                        >
                          <div className="font-bold">{c.name}</div>
                          <div className="text-xs text-[#52525B] font-mono">{c.phone}{c.address ? ` · ${c.address.slice(0, 40)}` : ""}</div>
                        </button>
                      ))}
                    </div>
                  )}
                  {custOpen && custSearch && filteredCustomers.length === 0 && (
                    <div className="absolute top-full left-0 right-0 z-20 mt-1 bg-white border border-[#E4E4E7] shadow-lg px-3 py-2 text-xs text-[#52525B]">Tidak ada pelanggan cocok. Tambah pelanggan baru di halaman Pelanggan.</div>
                  )}
                </div>
                {form.customer_id && (() => {
                  const c = customers.find((x) => x.id === form.customer_id);
                  if (!c || (!c.address && !c.city && !c.phone)) return null;
                  return (
                    <>
                      {(c.address || c.city) && (
                        <p className="text-[11px] text-[#52525B] mt-1.5 flex items-start gap-1" data-testid="order-customer-address">
                          <MapPin size={11} className="mt-0.5 shrink-0 text-[#002FA7]" />
                          <span>{[c.address, c.city].filter(Boolean).join(", ")}</span>
                        </p>
                      )}
                      {c.phone && (
                        <a
                          href={buildWaLink(c.phone, "")}
                          target="_blank"
                          rel="noreferrer"
                          data-testid="order-customer-wa-link"
                          className="mt-2 inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold text-white bg-[#059669] hover:bg-[#047857]"
                        >
                          <MessageCircle size={13} /> Chat WA Pelanggan
                        </a>
                      )}
                    </>
                  );
                })()}
              </Field>
              <Field label="Jadwal Pengerjaan" required>
                <input type="date" data-testid="order-schedule-input" value={form.schedule_date} onChange={(e) => setForm({ ...form, schedule_date: e.target.value })} required className="w-full px-3 py-2 border border-[#E4E4E7] text-sm outline-none focus:border-[#002FA7] font-mono" />
                <label className={`mt-2 flex items-center gap-2 border px-3 py-2 cursor-pointer ${form.luar_kota ? "border-[#7C3AED] bg-[#7C3AED]/5" : "border-[#E4E4E7] hover:bg-[#F4F4F5]"}`}>
                  <input type="checkbox" data-testid="order-luar-kota-checkbox" checked={!!form.luar_kota} onChange={(e) => setForm({ ...form, luar_kota: e.target.checked })} className="accent-[#7C3AED]" />
                  <span className="text-xs font-bold">Luar Kota</span>
                  <span className="text-[10px] text-[#52525B]">— payroll teknisi hari ini jadi Luar Kota: Ya</span>
                </label>
                <div className="mt-2 flex items-center gap-2">
                  <span className="text-[10px] font-bold uppercase tracking-wider text-[#52525B]">Kendaraan:</span>
                  {["motor", "mobil"].map((v) => (
                    <label key={v} className={`flex items-center gap-1.5 border px-3 py-1.5 cursor-pointer ${(form.vehicle || "motor") === v ? "border-[#002FA7] bg-[#002FA7]/5" : "border-[#E4E4E7] hover:bg-[#F4F4F5]"}`}>
                      <input type="radio" name="order-vehicle" data-testid={`order-vehicle-${v}`} checked={(form.vehicle || "motor") === v} onChange={() => setForm({ ...form, vehicle: v })} className="accent-[#002FA7]" />
                      <span className="text-xs font-bold capitalize">{v}</span>
                    </label>
                  ))}
                  <span className="text-[10px] text-[#52525B]">— dipakai hitung uang bensin Jarak Tempuh (mobil tidak dihitung)</span>
                </div>
              </Field>
              <div className="md:col-span-2">
                <div className="flex items-center justify-between mb-2">
                  <label className="label-cap">Teknisi yang Mengerjakan (bisa lebih dari 1)</label>
                  <div className="flex gap-2">
                    <button type="button" onClick={splitFeeEvenly} data-testid="order-split-fee" className="text-xs font-bold uppercase text-[#002FA7]">Bagi Fee Rata</button>
                    <button type="button" onClick={addAssignment} data-testid="order-add-assignment" className="text-xs font-bold uppercase text-[#002FA7] flex items-center gap-1"><Plus size={12} /> Tambah Teknisi</button>
                  </div>
                </div>
                {(form.assignments || []).length === 0 && (
                  <div className="border border-dashed border-[#E4E4E7] p-3 text-xs text-[#52525B] text-center">Belum ada teknisi ditugaskan. Klik "Tambah Teknisi".</div>
                )}
                {(() => {
                  const conflicts = (form.assignments || []).filter((a) => a.technician_id && leaveIdsForDate.includes(a.technician_id));
                  if (conflicts.length === 0) return null;
                  const names = conflicts.map((c) => technicians.find((t) => t.id === c.technician_id)?.name || "?").join(", ");
                  return (
                    <div className="border border-[#F59E0B] bg-[#FEF3C7] p-2 mb-2 text-xs text-[#92400E] flex items-start gap-2" data-testid="order-leave-warning">
                      <span className="font-bold">⚠️ Perhatian:</span>
                      <span>Teknisi <b>{names}</b> tercatat LIBUR pada tanggal {form.schedule_date}. Anda masih bisa menugaskan, tapi mohon konfirmasi ulang.</span>
                    </div>
                  );
                })()}
                {(form.assignments || []).map((a, idx) => {
                  const isLeave = a.technician_id && leaveIdsForDate.includes(a.technician_id);
                  return (
                  <div key={idx} className={`grid grid-cols-12 gap-2 mb-2 items-center ${isLeave ? "bg-[#FEF3C7]" : ""}`} data-testid={`assignment-${idx}`}>
                    <select value={a.technician_id} onChange={(e) => setAssignment(idx, { technician_id: e.target.value })} className={`col-span-7 px-2 py-1.5 border text-sm bg-white ${isLeave ? "border-[#F59E0B]" : "border-[#E4E4E7]"}`} data-testid={`assignment-tech-${idx}`}>
                      <option value="">— Pilih teknisi —</option>
                      {technicians.filter((t) => (t.role || "teknisi") === "teknisi").map(t => <option key={t.id} value={t.id}>{t.name}{leaveIdsForDate.includes(t.id) ? " (LIBUR)" : ""}</option>)}
                    </select>
                    <input type="number" value={a.fee} onChange={(e) => setAssignment(idx, { fee: e.target.value })} placeholder="Fee" className="col-span-4 px-2 py-1.5 border border-[#E4E4E7] text-sm font-mono" data-testid={`assignment-fee-${idx}`} />
                    <button type="button" onClick={() => removeAssignment(idx)} className="col-span-1 text-[#DC2626] flex justify-center" data-testid={`assignment-remove-${idx}`}><Trash2 size={14} /></button>
                  </div>
                  );
                })}
              </div>
              <div className="md:col-span-2">
                <div className="flex items-center justify-between mb-2">
                  <label className="label-cap">Sesi Pengerjaan Multi-Hari <span className="normal-case font-normal text-[#A1A1AA]">(opsional)</span></label>
                  <button type="button" onClick={addSession} data-testid="order-add-session" className="text-xs font-bold uppercase text-[#7C3AED] flex items-center gap-1"><Plus size={12} /> Tambah Hari</button>
                </div>
                {(form.work_sessions || []).length === 0 ? (
                  <div className="border border-dashed border-[#E4E4E7] p-3 text-[10px] text-[#52525B]">
                    Bila order dikerjakan lebih dari 1 hari (bisa dengan teknisi berbeda tiap harinya), klik "Tambah Hari" dan isi tanggal + teknisi hari itu.
                    Poin payroll dibagi rata per hari sesi, uang bensin dihitung per hari kunjungan, dan order tampil di semua tanggal sesinya di kalender.
                  </div>
                ) : (
                  <div className="space-y-2">
                    {(form.work_sessions || []).map((s, idx) => (
                      <div key={idx} className="border border-[#E4E4E7] p-2" data-testid={`order-session-${idx}`}>
                        <div className="flex items-center gap-2 mb-1.5">
                          <span className="text-[10px] font-bold uppercase text-[#7C3AED]">Hari {idx + 1}</span>
                          <input type="date" value={s.date} onChange={(e) => setSessionDate(idx, e.target.value)} className="px-2 py-1 border border-[#E4E4E7] text-xs font-mono outline-none focus:border-[#7C3AED]" data-testid={`order-session-date-${idx}`} />
                          <button type="button" onClick={() => removeSession(idx)} className="ml-auto text-[#DC2626] p-1" data-testid={`order-session-remove-${idx}`}><Trash2 size={13} /></button>
                        </div>
                        <div className="flex flex-wrap gap-1">
                          {technicians.filter((t) => (t.role || "teknisi") === "teknisi").map((t) => {
                            const on = (s.technician_ids || []).includes(t.id);
                            return (
                              <button
                                key={t.id}
                                type="button"
                                onClick={() => toggleSessionTech(idx, t.id)}
                                data-testid={`order-session-${idx}-tech-${t.id}`}
                                className={`text-[10px] font-bold px-2 py-1 border transition-colors ${on ? "bg-[#7C3AED] text-white border-[#7C3AED]" : "bg-white text-[#52525B] border-[#E4E4E7] hover:border-[#7C3AED]/50"}`}
                              >
                                {t.name}
                              </button>
                            );
                          })}
                        </div>
                        {(s.technician_ids || []).length === 0 && <div className="text-[9px] text-[#DC2626] mt-1">Pilih minimal 1 teknisi untuk hari ini.</div>}
                      </div>
                    ))}
                    <div className="text-[9px] text-[#52525B]">Hari pertama sebaiknya sama dengan Jadwal Pengerjaan utama ({form.schedule_date}).</div>
                  </div>
                )}
              </div>
              <Field label="Status Pekerjaan">
                <select data-testid="order-status-select" value={form.work_status} onChange={(e) => setForm({ ...form, work_status: e.target.value })} className="w-full px-3 py-2 border border-[#E4E4E7] text-sm bg-white outline-none focus:border-[#002FA7]">
                  {WORK_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </Field>
              <Field label="Status Pembayaran">
                <select data-testid="order-pay-status-select" value={form.payment_status} onChange={(e) => setForm({ ...form, payment_status: e.target.value })} className="w-full px-3 py-2 border border-[#E4E4E7] text-sm bg-white outline-none focus:border-[#002FA7]">
                  {PAY_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </Field>
              <Field label="Cara Pembayaran">
                <select data-testid="order-pay-method-select" value={form.payment_method} onChange={(e) => setForm({ ...form, payment_method: e.target.value, bank_destination: e.target.value === "Transfer" ? form.bank_destination : "" })} className="w-full px-3 py-2 border border-[#E4E4E7] text-sm bg-white outline-none focus:border-[#002FA7]">
                  {PAY_METHODS.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </Field>

              {form.payment_status === "DP" && (
                <Field label="DP yang Sudah Dibayar (Rp)" colSpan required>
                  <input
                    type="number"
                    data-testid="order-dp-amount-input"
                    value={form.dp_amount}
                    onChange={(e) => setForm({ ...form, dp_amount: Number(e.target.value || 0) })}
                    min="0"
                    required
                    placeholder="0"
                    className="w-full px-3 py-2 border border-[#E4E4E7] text-sm font-mono outline-none focus:border-[#002FA7]"
                  />
                  {form.dp_amount > 0 && (() => {
                    const total = (form.items || []).reduce((s, i) => s + lineTotal(i), 0);
                    const sisa = Math.max(0, total - Number(form.dp_amount || 0));
                    return (
                      <p className="text-[10px] text-[#52525B] mt-1">
                        Total order: <span className="font-mono font-bold">{formatIDR(total)}</span> · Sisa pelunasan: <span className="font-mono font-bold text-[#DC2626]">{formatIDR(sisa)}</span>
                      </p>
                    );
                  })()}
                </Field>
              )}

              {form.payment_method === "Transfer" && (
                <Field label="Bank Tujuan Transfer" required colSpan>
                  <select
                    data-testid="order-bank-destination-select"
                    value={form.bank_destination}
                    onChange={(e) => setForm({ ...form, bank_destination: e.target.value })}
                    required
                    className="w-full px-3 py-2 border border-[#E4E4E7] text-sm bg-white outline-none focus:border-[#002FA7]"
                  >
                    <option value="">— Pilih bank tujuan —</option>
                    {(settings.bank_name || settings.bank_account_no) && (
                      <option value="1">
                        {settings.bank_name || "Bank 1"}{settings.bank_account_no ? ` · ${settings.bank_account_no}` : ""}{settings.bank_account_holder ? ` (a.n. ${settings.bank_account_holder})` : ""}
                      </option>
                    )}
                    {(settings.bank_name_2 || settings.bank_account_no_2) && (
                      <option value="2">
                        {settings.bank_name_2 || "Bank 2"}{settings.bank_account_no_2 ? ` · ${settings.bank_account_no_2}` : ""}{settings.bank_account_holder_2 ? ` (a.n. ${settings.bank_account_holder_2})` : ""}
                      </option>
                    )}
                  </select>
                  {!(settings.bank_name || settings.bank_account_no) && !(settings.bank_name_2 || settings.bank_account_no_2) && (
                    <p className="text-[10px] text-[#DC2626] mt-1">Isi data rekening dulu di halaman Pengaturan.</p>
                  )}
                </Field>
              )}

              <Field label="Masa Garansi">
                <select data-testid="order-warranty-select" value={form.warranty_period} onChange={(e) => setForm({ ...form, warranty_period: e.target.value })} className="w-full px-3 py-2 border border-[#E4E4E7] text-sm bg-white outline-none focus:border-[#002FA7]">
                  {WARRANTY_OPTIONS.map(w => <option key={w.value} value={w.value}>{w.label}</option>)}
                </select>
                {form.warranty_period && addWarrantyPeriod(form.schedule_date, form.warranty_period) && (
                  <p className="text-[10px] text-[#52525B] mt-1">Garansi berlaku s/d {addWarrantyPeriod(form.schedule_date, form.warranty_period)}</p>
                )}
              </Field>

              {form.work_status === "Komplainan" && (
                <Field label="Order Terkait (Komplainan)" colSpan>
                  <select
                    data-testid="order-related-select"
                    value={form.related_order_id}
                    onChange={(e) => setForm({ ...form, related_order_id: e.target.value })}
                    className="w-full px-3 py-2 border border-[#E4E4E7] text-sm bg-white outline-none focus:border-[#002FA7]"
                  >
                    <option value="">— Pilih order yang dikomplain —</option>
                    {list.filter(x => x.id !== editingId && x.customer_id === form.customer_id).map(x => (
                      <option key={x.id} value={x.id}>{x.order_no} · {x.schedule_date} · {x.work_status}</option>
                    ))}
                  </select>
                  {form.customer_id && list.filter(x => x.id !== editingId && x.customer_id === form.customer_id).length === 0 && (
                    <p className="text-[10px] text-[#DC2626] mt-1">Tidak ada order lain dari pelanggan ini.</p>
                  )}
                </Field>
              )}

              <div className="md:col-span-2 mt-2">
                <div className="flex items-center justify-between mb-2">
                  <label className="label-cap">Jasa / Produk</label>
                  <button type="button" onClick={addItem} data-testid="order-add-item-button" className="text-xs font-bold uppercase text-[#002FA7] flex items-center gap-1"><Plus size={12} /> Tambah Item</button>
                </div>
                <div className="border border-[#E4E4E7]">
                  <div className="grid grid-cols-12 gap-2 px-3 py-2 bg-[#F4F4F5] border-b border-[#E4E4E7] label-cap text-[10px]">
                    <div className="col-span-6 sm:col-span-5">Deskripsi · Pilih dari Katalog</div>
                    <div className="col-span-2 sm:col-span-1">Qty</div>
                    <div className="col-span-3 sm:col-span-3">Harga</div>
                    <div className="hidden sm:block sm:col-span-2">Point</div>
                    <div className="col-span-1"></div>
                  </div>
                  <datalist id="catalog-list">
                    {catalog.jasa.map((c) => (
                      <option key={`j-${c.id}`} value={c.name}>{`JASA · ${formatIDR(c.price)} · ${c.point} pts`}</option>
                    ))}
                    {catalog.material.map((c) => (
                      <option key={`m-${c.id}`} value={c.name}>{`MATERIAL · ${formatIDR(c.price)}`}</option>
                    ))}
                  </datalist>
                  {form.items.map((it, idx) => (
                    <div key={idx} className="border-b border-[#E4E4E7] last:border-b-0" data-testid={`order-item-${idx}`}>
                      <div className="grid grid-cols-12 gap-2 px-3 py-2 items-center">
                        <input
                          list="catalog-list"
                          value={it.description}
                          onChange={(e) => {
                            const name = e.target.value;
                            const found = [...catalog.jasa, ...catalog.material].find(c => c.name === name);
                            if (found) setItem(idx, { description: name, price: found.price, point: found.point, kind: found.kind });
                            else setItem(idx, { description: name });
                          }}
                          placeholder="Pilih jasa/material atau ketik bebas…"
                          className="col-span-6 sm:col-span-5 px-2 py-1.5 border border-[#E4E4E7] text-sm outline-none focus:border-[#002FA7]"
                          data-testid={`order-item-desc-${idx}`}
                        />
                        <input type="number" value={it.qty} onChange={(e) => setItem(idx, { qty: e.target.value })} className="col-span-2 sm:col-span-1 px-2 py-1.5 border border-[#E4E4E7] text-sm font-mono outline-none focus:border-[#002FA7]" data-testid={`order-item-qty-${idx}`} />
                        <input type="number" value={it.price} onChange={(e) => setItem(idx, { price: e.target.value })} className="col-span-3 px-2 py-1.5 border border-[#E4E4E7] text-sm font-mono outline-none focus:border-[#002FA7]" data-testid={`order-item-price-${idx}`} />
                        <input type="number" value={it.point ?? 0} onChange={(e) => setItem(idx, { point: e.target.value })} placeholder="0" title="Bonus Point untuk teknisi" className="hidden sm:block sm:col-span-2 px-2 py-1.5 border border-[#E4E4E7] text-xs font-mono outline-none focus:border-[#002FA7]" data-testid={`order-item-point-${idx}`} />
                        <button type="button" onClick={() => removeItem(idx)} className="col-span-1 text-[#DC2626] flex justify-center" data-testid={`order-item-remove-${idx}`}><Trash2 size={14} /></button>
                      </div>
                      <div className="flex items-center gap-2 px-3 pb-2 flex-wrap">
                        <span className="text-[10px] font-bold uppercase tracking-wider text-[#52525B]">Diskon:</span>
                        <select
                          value={it.disc_type || ""}
                          onChange={(e) => setItem(idx, { disc_type: e.target.value, disc_value: e.target.value ? it.disc_value || 0 : 0 })}
                          className="px-2 py-1 border border-[#E4E4E7] text-xs bg-white font-bold outline-none focus:border-[#002FA7]"
                          data-testid={`order-item-disc-type-${idx}`}
                        >
                          <option value="">Tanpa Diskon</option>
                          <option value="percent">Persen (%)</option>
                          <option value="amount">Nominal (Rp)</option>
                        </select>
                        {it.disc_type && (
                          <div className="flex items-center gap-1">
                            <input
                              type="number"
                              min="0"
                              max={it.disc_type === "percent" ? 100 : undefined}
                              value={it.disc_value || ""}
                              onChange={(e) => setItem(idx, { disc_value: e.target.value })}
                              placeholder={it.disc_type === "percent" ? "%" : "Rp"}
                              className="w-24 px-2 py-1 border border-[#E4E4E7] text-xs font-mono outline-none focus:border-[#002FA7]"
                              data-testid={`order-item-disc-value-${idx}`}
                            />
                            <span className="text-[10px] font-bold text-[#52525B]">{it.disc_type === "percent" ? "%" : "Rp"}</span>
                          </div>
                        )}
                        <span className="ml-auto text-[10px] text-[#52525B]">
                          Subtotal: <span className={`font-mono font-bold ${Number(it.disc_value || 0) > 0 && it.disc_type ? "text-[#059669]" : ""}`} data-testid={`order-item-subtotal-${idx}`}>{formatIDR(lineTotal(it))}</span>
                          {it.disc_type && Number(it.disc_value || 0) > 0 && (
                            <span className="line-through ml-1 text-[#A1A1AA] font-mono">{formatIDR(Number(it.qty || 0) * Number(it.price || 0))}</span>
                          )}
                        </span>
                      </div>
                    </div>
                  ))}
                  <div className="px-3 py-3 bg-[#F4F4F5] flex items-center justify-between">
                    <span className="label-cap">Total</span>
                    <span className="font-heading text-3xl font-black tracking-tighter font-mono" data-testid="order-total">{formatIDR(total)}</span>
                  </div>
                </div>
              </div>

              <Field label="Catatan" colSpan>
                <textarea data-testid="order-notes-input" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} className="w-full px-3 py-2 border border-[#E4E4E7] text-sm outline-none focus:border-[#002FA7]" />
              </Field>

              {editingId && (() => {
                const fid = editingOrder?.video_folder_id || editingOrder?.gdrive_folder_id || "";
                const fname = editingOrder?.video_folder_name || "";
                return (
                  <div className="md:col-span-2 border border-[#E4E4E7] bg-[#FAFAFA] p-3" data-testid="order-drive-section">
                    <div className="label-cap mb-2 flex items-center gap-1.5"><FolderOpen size={12} /> Dokumentasi & Video (Google Drive)</div>
                    <div className="flex items-center gap-2 flex-wrap text-xs">
                      {fid ? (
                        <>
                          <span className="px-2 py-1 border border-[#16A34A]/40 bg-[#16A34A]/5 text-[#15803D] font-bold" data-testid="order-drive-folder-name">
                            {fname || "Folder otomatis (dibuat aplikasi)"}
                          </span>
                          <a href={`https://drive.google.com/drive/folders/${fid}`} target="_blank" rel="noreferrer" className="font-bold text-[#002FA7] hover:underline" data-testid="order-drive-open-link">Buka Folder ↗</a>
                          <button type="button" disabled={driveBusy} onClick={resetDriveFolder} className="text-[#DC2626] font-bold hover:underline disabled:opacity-50" data-testid="order-drive-reset-btn">Reset ke Otomatis</button>
                        </>
                      ) : (
                        <span className="text-[#52525B]" data-testid="order-drive-auto-label">Mode otomatis — folder dicari/dibuat sendiri saat upload dokumentasi atau cek video payroll.</span>
                      )}
                      <button type="button" onClick={() => { setDrivePanelOpen((v) => !v); setDriveResults([]); }} className="font-bold text-[#002FA7] hover:underline" data-testid="order-drive-pick-btn">
                        {drivePanelOpen ? "Tutup Pencarian" : "Pilih Folder Sendiri…"}
                      </button>
                      <button type="button" onClick={() => setDocsFor(editingOrder)} className="font-bold text-[#002FA7] hover:underline" data-testid="order-drive-files-btn">Kelola File</button>
                      <button type="button" onClick={() => setVideoMergeFor(editingOrder)} className="font-bold text-[#002FA7] hover:underline" data-testid="order-video-merge-btn">🎬 Gabung Video</button>
                    </div>
                    {drivePanelOpen && (
                      <div className="mt-2 border border-[#E4E4E7] bg-white p-2" data-testid="order-drive-search-panel">
                        <div className="flex gap-2">
                          <input
                            value={driveSearch}
                            onChange={(e) => setDriveSearch(e.target.value)}
                            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); searchDriveFolders(); } }}
                            placeholder="Cari nama folder di Drive… (mis. nama pelanggan)"
                            className="flex-1 px-2 py-1.5 border border-[#E4E4E7] text-xs outline-none focus:border-[#002FA7]"
                            data-testid="order-drive-search-input"
                          />
                          <button type="button" disabled={driveBusy} onClick={searchDriveFolders} className="px-3 py-1.5 text-xs font-bold uppercase bg-[#002FA7] text-white disabled:opacity-50" data-testid="order-drive-search-btn">{driveBusy ? "..." : "Cari"}</button>
                        </div>
                        {driveResults.length > 0 && (
                          <div className="mt-2 max-h-40 overflow-y-auto divide-y divide-[#F4F4F5]">
                            {driveResults.map((f) => (
                              <button key={f.id} type="button" disabled={driveBusy} onClick={() => pickDriveFolder(f)} className="w-full text-left px-2 py-1.5 text-xs hover:bg-[#F4F4F5] flex items-center gap-2 disabled:opacity-50" data-testid={`order-drive-folder-option-${f.id}`}>
                                <FolderOpen size={12} className="shrink-0 text-[#EAB308]" /> {f.name}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                    <p className="text-[10px] text-[#52525B] mt-2">Folder ini dipakai untuk file dokumentasi DAN perhitungan total durasi video di Payroll.</p>
                  </div>
                );
              })()}
            </div>
            <div className="px-6 py-4 border-t border-[#E4E4E7] flex flex-wrap items-center justify-end gap-2">
              {editingId && (
                <button
                  type="button"
                  data-testid="order-modal-send-invoice"
                  disabled={form.work_status !== "Selesai"}
                  title={form.work_status !== "Selesai" ? "Ubah status pekerjaan menjadi Selesai dulu (lalu Simpan) agar bisa kirim invoice" : "Kirim invoice via WhatsApp"}
                  onClick={() => sendInvoice({ ...editingOrder, work_status: form.work_status, payment_status: form.payment_status, payment_method: form.payment_method })}
                  className="mr-auto px-4 py-2 text-xs font-bold uppercase border flex items-center gap-1.5 border-[#002FA7] text-[#002FA7] hover:bg-[#002FA7]/10 disabled:opacity-40 disabled:cursor-not-allowed disabled:border-[#E4E4E7] disabled:text-[#A1A1AA]"
                >
                  <FileText size={13} /> Kirim Invoice WA
                </button>
              )}
              <button type="button" onClick={() => setModal(false)} className="px-4 py-2 text-xs font-bold uppercase border border-[#E4E4E7]">Batal</button>
              <button type="submit" data-testid="order-submit-button" className="px-4 py-2 text-xs font-bold uppercase bg-[#002FA7] text-white hover:bg-[#00227A]">Simpan Order</button>
            </div>
          </form>
        </div>
      )}

      {confirmDel && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50" onClick={() => setConfirmDel(null)}>
          <div onClick={(e) => e.stopPropagation()} className="bg-white border border-[#E4E4E7] max-w-sm w-full p-6">
            <h3 className="font-heading text-lg font-bold mb-2">Hapus Order?</h3>
            <p className="text-sm text-[#52525B] mb-6">"{confirmDel.order_no}" akan dihapus permanen.</p>
            <div className="flex justify-end gap-2">
              <button onClick={() => setConfirmDel(null)} className="px-4 py-2 text-xs font-bold uppercase border border-[#E4E4E7]">Batal</button>
              <button onClick={remove} data-testid="confirm-delete-order-button" className="px-4 py-2 text-xs font-bold uppercase bg-[#DC2626] text-white">Hapus</button>
            </div>
          </div>
        </div>
      )}

      {videoMergeFor && (
        <VideoMergeModal
          orderId={videoMergeFor.id}
          title={[videoMergeFor.order_no, videoMergeFor.customer_name].filter(Boolean).join(" · ")}
          onClose={() => setVideoMergeFor(null)}
        />
      )}
      {docsFor && (
        <OrderDocsModal order={docsFor} onClose={() => setDocsFor(null)} />
      )}
    </Layout>
  );
};

const Field = ({ label, children, required, colSpan }) => (
  <div className={colSpan ? "md:col-span-2" : ""}>
    <label className="label-cap block mb-1.5">{label}{required && <span className="text-[#DC2626]">*</span>}</label>
    {children}
  </div>
);

export default Orders;
